import React, { Suspense, useLayoutEffect, useEffect, useState } from 'react'
import { BrowserRouter as Router, Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { ContactProvider } from './context/ContactContext';
import { SiteDataProvider } from './context/SiteDataContext';
// Shown while a code-split page chunk loads. Imported eagerly on purpose: a lazy
// fallback cannot render itself.
import PageLoader from './components/layouts/PageLoader';

// Home
const Home = React.lazy(() => import("./components/pages/Home"));
const Hometwo = React.lazy(() => import("./components/pages/Hometwo"));
const Homethree = React.lazy(() => import("./components/pages/Homethree"));
// About
const About = React.lazy(() => import("./components/pages/About"));
// Blog
const Bloggrid = React.lazy(() => import("./components/pages/Bloggrid"));
const Blogdetails = React.lazy(() => import("./components/pages/Blogdetails"));
const Testimonies = React.lazy(() => import("./components/pages/Testimonies"));
const TrainingClasses = React.lazy(() => import("./components/pages/TrainingClasses"));
// Pages
const Broadcast = React.lazy(() => import("./components/pages/Broadcast"));
// Volunteers
const Volunteers = React.lazy(() => import("./components/pages/Volunteers"));
const Volunteerdetails = React.lazy(() => import("./components/pages/Volunteerdetails"));
// Pastorate
const Pastorate = React.lazy(() => import("./components/pages/Pastorate"));
const PastPastors = React.lazy(() => import("./components/pages/PastPastors"));
const Pastoratedetails = React.lazy(() => import("./components/pages/Pastoratedetails"));
// FAQ's
const Faqs = React.lazy(() => import("./components/pages/Faqs"));
// Events
const Events = React.lazy(() => import("./components/pages/Events"));
const Eventdetails = React.lazy(() => import("./components/pages/Eventdetails"));
// Sermons
const Sermons = React.lazy(() => import("./components/pages/Sermons"));
const Sermondetails = React.lazy(() => import("./components/pages/Sermondetails"));
// Media Centre
const MediaCenter = React.lazy(() => import("./components/pages/MediaCenter"));
// Audio Library
const AudioLibrary = React.lazy(() => import("./components/pages/AudioLibrary"));
// Gallery
const Gallery = React.lazy(() => import("./components/pages/Gallery"));
// Ministry
const Ministries = React.lazy(() => import("./components/pages/Ministries"));
const Ministry = React.lazy(() => import("./components/pages/Ministry"));
const MinistryActivity = React.lazy(() => import("./components/pages/MinistryActivity"));
const Ministrydetails = React.lazy(() => import("./components/pages/Ministrydetails"));
// Services
const Servicestwo = React.lazy(() => import("./components/pages/Servicestwo"));
// Donation
const Donation = React.lazy(() => import("./components/pages/Donation"));
const Donationgrid = React.lazy(() => import("./components/pages/Donationgrid"));
// Shop
const Shop = React.lazy(() => import("./components/pages/Shop"));
const Shopdetails = React.lazy(() => import("./components/pages/Shopdetails"));
const Wishlist = React.lazy(() => import("./components/pages/Wishlist"));
const Cart = React.lazy(() => import("./components/pages/Cart"));
const Checkout = React.lazy(() => import("./components/pages/Checkout"));
// Contact
const Contact = React.lazy(() => import("./components/pages/Contact"));
// Bible Studies
const BibleStudies = React.lazy(() => import("./components/pages/BibleStudies"));
// Messages Hub
const Messages = React.lazy(() => import("./components/pages/Messages"));
// Natural Groups
const NaturalGroups = React.lazy(() => import("./components/pages/NaturalGroups"));
const NaturalGroup = React.lazy(() => import("./components/pages/NaturalGroup"));
const MajorProgramme = React.lazy(() => import("./components/pages/MajorProgramme"));
// Error 404
const Errorpage = React.lazy(() => import("./components/pages/Errorpage"));
const ServiceTimesPage = React.lazy(() => import("./components/pages/ServiceTimes"));

// Admin Panel
const AdminLogin = React.lazy(() => import("./components/admin/AdminLogin"));
const Dashboard = React.lazy(() => import("./components/admin/Dashboard"));
const EventsManager = React.lazy(() => import("./components/admin/EventsManager"));
const SermonsManager = React.lazy(() => import("./components/admin/SermonsManager"));
const BlogManager = React.lazy(() => import("./components/admin/BlogManager"));
const TestimoniesManager = React.lazy(() => import("./components/admin/TestimoniesManager"));
const ServiceTimesManager = React.lazy(() => import("./components/admin/ServiceTimesManager"));
const BannersManager = React.lazy(() => import("./components/admin/BannersManager"));
const ContactManager = React.lazy(() => import("./components/admin/ContactManager"));
const MediaLibrary = React.lazy(() => import("./components/admin/MediaLibrary"));
const PastorateManager = React.lazy(() => import("./components/admin/PastorateManager"));
const WelcomeManager = React.lazy(() => import("./components/admin/WelcomeManager"));
const AboutPageManager = React.lazy(() => import('./components/admin/AboutPageManager'));
const ThemeManager = React.lazy(() => import("./components/admin/ThemeManager"));
const DiggingDeepManager = React.lazy(() => import("./components/admin/DiggingDeepManager"));
const MinistryManager = React.lazy(() => import("./components/admin/MinistryManager"));
const MinistryActivityManager = React.lazy(() => import("./components/admin/MinistryActivityManager"));
const NaturalGroupManager = React.lazy(() => import("./components/admin/NaturalGroupManager"));
const AudioLibraryManager = React.lazy(() => import('./components/admin/AudioLibraryManager'));
const HouseFellowshipManager = React.lazy(() => import('./components/admin/HouseFellowshipManager'));
const GivingManager = React.lazy(() => import('./components/admin/GivingManager'));
const GalleryManager = React.lazy(() => import('./components/admin/GalleryManager'));
const ParishesManager = React.lazy(() => import('./components/admin/ParishesManager'));
const TrainingClassesManager = React.lazy(() => import('./components/admin/TrainingClassesManager'));
const MajorProgrammesManager = React.lazy(() => import('./components/admin/MajorProgrammesManager'));
const AdminHelp = React.lazy(() => import('./components/admin/AdminHelp'));

// Scroll to Top
const ScrollToTop = withRouter(({ children, location: { pathname } }) => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return children || null
})


function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [swRegistration, setSwRegistration] = useState(null);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  useEffect(() => {
    const darken = (hex, amount = 30) => {
      const n = parseInt(hex.replace('#', ''), 16);
      const r = Math.max(0, (n >> 16) - amount);
      const g = Math.max(0, ((n >> 8) & 0xff) - amount);
      const b = Math.max(0, (n & 0xff) - amount);
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    };
    const applyTheme = (color) => {
      document.documentElement.style.setProperty('--thm-base', color);
      document.documentElement.style.setProperty('--thm-base-dark', darken(color));
    };
    const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    fetch(`${API}/settings`)
      .then(r => r.json())
      .then(data => {
        applyTheme((data.success && data.settings && data.settings.theme_color) || '#b01933');
      })
      .catch(() => applyTheme('#b01933'));
  }, []);

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    const onUpdateReady = (event) => {
      setSwRegistration(event.detail?.registration || null);
      setShowUpdateBanner(true);
    };
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      window.rccgDeferredInstallPrompt = event;
      setInstallPromptEvent(event);
      setShowInstallBanner(true);
    };
    const onInstalled = () => {
      window.rccgDeferredInstallPrompt = null;
      setInstallPromptEvent(null);
      setShowInstallBanner(false);
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('rccg-sw-update', onUpdateReady);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('rccg-sw-update', onUpdateReady);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const applyAppUpdate = () => {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setShowUpdateBanner(false);
    window.location.reload();
  };

  const installApp = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    if (choice && choice.outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    window.rccgDeferredInstallPrompt = null;
    setInstallPromptEvent(null);
  };

  return (
    <ContactProvider>
    <SiteDataProvider>
    {isOffline && (
      <div className="pwa-status-banner pwa-status-banner-offline">
        You are offline. Previously loaded pages remain available.
      </div>
    )}
    {showUpdateBanner && (
      <div className="pwa-status-banner pwa-status-banner-update">
        <span>A new version of this app is ready.</span>
        <div className="pwa-status-actions">
          <button type="button" className="pwa-status-btn" onClick={applyAppUpdate}>Update now</button>
          <button type="button" className="pwa-status-btn pwa-status-btn-secondary" onClick={() => setShowUpdateBanner(false)}>Later</button>
        </div>
      </div>
    )}
    {showInstallBanner && installPromptEvent && (
      <div className="pwa-status-banner pwa-status-banner-install">
        <span>Install RCCG Dominion app for quick access on your phone.</span>
        <div className="pwa-status-actions">
          <button type="button" className="pwa-status-btn" onClick={installApp}>Install App</button>
          <button type="button" className="pwa-status-btn pwa-status-btn-secondary" onClick={() => setShowInstallBanner(false)}>Not now</button>
        </div>
      </div>
    )}
    <Router>
      <Suspense fallback={<PageLoader />}>
        <ScrollToTop>
          <Switch>
            {/* Home */}
            <Route exact path="/" component={Hometwo} />
            <Route exact path="/home-v1" component={Home} />
            <Route exact path="/home-v2" component={Hometwo} />
            <Route exact path="/home-v3" component={Homethree} />
            {/* About */}
            <Route exact path="/about" component={About} />
            {/* Blog */}
            <Route exact path="/blog/cat/:catId" component={props => (<Bloggrid {...props} key={window.location.pathname} />)} />
            <Route exact path="/blog/tag/:tagId" component={props => (<Bloggrid {...props} key={window.location.pathname} />)} />
            <Route exact path="/blog/author/:authorId" component={props => (<Bloggrid {...props} key={window.location.pathname} />)} />
            <Route exact path="/blog/search/:query" component={props => (<Bloggrid {...props} key={window.location.pathname} />)} />
            <Route exact path="/blog-grid" component={Bloggrid} />
            <Route exact path="/articles" component={Bloggrid} />
            <Route exact path="/blog-details/:id" component={props => (<Blogdetails {...props} key={window.location.pathname} />)} />
            <Route exact path="/articles/:id" component={props => (<Blogdetails {...props} key={window.location.pathname} />)} />
            <Route exact path="/testimonies" component={Testimonies} />
            <Route exact path="/training-classes" component={TrainingClasses} />
            {/* Pages */}
            <Route exact path="/broadcast" component={Broadcast} />
            {/* Volunteers */}
            <Route exact path="/volunteers" component={Volunteers} />
            <Route exact path="/volunteer-details/:id" component={props => (<Volunteerdetails {...props} key={window.location.pathname} />)} />
            {/* Pastorate */}
            <Route exact path="/pastorate" component={Pastorate} />
            <Route exact path="/past-pastors" component={PastPastors} />
            <Route exact path="/pastorate-details/:id" component={props => (<Pastoratedetails {...props} key={window.location.pathname} />)} />
            {/* FAQ's */}
            <Route exact path="/faqs" component={Faqs} />
            {/* Events */}
            <Route exact path="/event/tag/:tagId" component={props => (<Events {...props} key={window.location.pathname} />)} />
            <Route exact path="/event/speaker/:speakerId" component={props => (<Events {...props} key={window.location.pathname} />)} />
            <Route exact path="/events" component={Events} />
            <Route exact path="/event-details/:id" component={props => (<Eventdetails {...props} key={window.location.pathname} />)} />
            {/* Service Times */}
            <Route exact path="/service-times" component={ServiceTimesPage} />
            {/* Sermons */}
            <Route exact path="/sermon/author/:authorId" component={props => (<Sermons {...props} key={window.location.pathname} />)} />
            <Route exact path="/sermons" component={Sermons} />
            <Route exact path="/sermon-details/:id" component={props => (<Sermondetails {...props} key={window.location.pathname} />)} />
            {/* Media Centre */}
            <Route exact path="/media" component={MediaCenter} />
            {/* Audio Library */}
            <Route exact path="/audio-library" component={AudioLibrary} />
            <Route exact path="/audio-messages" render={() => <Redirect to="/audio-library" />} />
            {/* Gallery */}
            <Route exact path="/gallery" component={Gallery} />
            {/* Ministry */}
            <Route exact path="/ministry/cat/:catId" component={props => (<Ministries {...props} key={window.location.pathname} />)} />
            <Route exact path="/ministry/client/:clientId" component={props => (<Ministries {...props} key={window.location.pathname} />)} />
            <Route exact path="/ministries" component={Ministries} />
            <Route exact path="/ministry/:slug" component={props => (<Ministry {...props} key={window.location.pathname} />)} />
            <Route exact path="/ministry/:slug/activities/:activitySlug" component={props => (<MinistryActivity {...props} key={window.location.pathname} />)} />
            <Route exact path="/ministry-details/:id" component={props => (<Ministrydetails {...props} key={window.location.pathname} />)} />
            {/* Services — legacy route kept as redirect to the canonical /service-times page */}
            <Route exact path="/services" render={() => <Redirect to="/service-times" />} />
            <Route exact path="/services-v2" component={Servicestwo} />
            {/* Donation */}
            <Route exact path="/donation" component={Donation} />
            <Route exact path="/donation-grid" component={Donationgrid} />
            {/* Shop */}
            <Route exact path="/shop/cat/:catId" component={props => (<Shop {...props} key={window.location.pathname} />)} />
            <Route exact path="/shop/tag/:tagId" component={props => (<Shop {...props} key={window.location.pathname} />)} />
            <Route exact path="/shop/search/:query" component={props => (<Shop {...props} key={window.location.pathname} />)} />
            <Route exact path="/shop/:minPrice/:maxPrice" component={props => (<Shop {...props} key={window.location.pathname} />)} />
            <Route exact path="/shop" component={Shop} />
            <Route exact path="/product-single/:id" component={props => (<Shopdetails {...props} key={window.location.pathname} />)} />
            <Route exact path="/wishlist" component={Wishlist} />
            <Route exact path="/cart" component={Cart} />
            <Route exact path="/checkout" component={Checkout} />
            {/* Contact */}
            <Route exact path="/contact" component={Contact} />
            {/* Bible Studies */}
            <Route exact path="/bible-studies" component={BibleStudies} />
            {/* Messages Hub */}
            <Route exact path="/messages" component={Messages} />
            {/* Natural Groups */}
            <Route exact path="/natural-groups" component={NaturalGroups} />
            <Route exact path="/natural-group/:slug" component={props => (<NaturalGroup {...props} key={window.location.pathname} />)} />
            {/* Major Programmes */}
            <Route exact path="/programmes/change-my-story" component={MajorProgramme} />
            {/* Error 404 */}
            {/* Admin Panel */}
            <Route exact path="/admin/login" component={AdminLogin} />
            <Route exact path="/admin" component={Dashboard} />
            <Route exact path="/admin/events" component={EventsManager} />
            <Route exact path="/admin/sermons" component={SermonsManager} />
            <Route exact path="/admin/blog" component={BlogManager} />
            <Route exact path="/admin/testimonies" component={TestimoniesManager} />
            <Route exact path="/admin/service-times" component={ServiceTimesManager} />
            <Route exact path="/admin/banners" component={BannersManager} />
            <Route exact path="/admin/contact" component={ContactManager} />
            <Route exact path="/admin/media" component={MediaLibrary} />
            <Route exact path="/admin/pastorate" component={PastorateManager} />
            <Route exact path="/admin/welcome" component={WelcomeManager} />
            <Route exact path="/admin/about" component={AboutPageManager} />
            <Route exact path="/admin/theme" component={ThemeManager} />
            <Route exact path="/admin/digging-deep" component={DiggingDeepManager} />
            <Route exact path="/admin/ministries" component={MinistryManager} />
            <Route exact path="/admin/ministry-activities" component={MinistryActivityManager} />
            <Route exact path="/admin/natural-groups" component={NaturalGroupManager} />
            <Route exact path="/admin/audio-library" component={AudioLibraryManager} />
            <Route exact path="/admin/audio-messages" render={() => <Redirect to="/admin/audio-library" />} />
            <Route exact path="/admin/house-fellowship" component={HouseFellowshipManager} />
            <Route exact path="/admin/giving" component={GivingManager} />
            <Route exact path="/admin/gallery" component={GalleryManager} />
            <Route exact path="/admin/parishes" component={ParishesManager} />
            <Route exact path="/admin/training-classes" component={TrainingClassesManager} />
            <Route exact path="/admin/major-programmes" component={MajorProgrammesManager} />
            <Route exact path="/admin/help" component={AdminHelp} />
            {/* Error 404 */}
            <Route exact path="/error-page" component={Errorpage} />
            <Route exact component={Errorpage} />
          </Switch>
        </ScrollToTop>
      </Suspense>
    </Router>
    </SiteDataProvider>
    </ContactProvider>
  );
}

export default App;
